/** Validated viewer-owned display snapshots. No TTL or background refresh. */
export type CardCacheScope={owner:string;context:string};
const maxEntries=2048,maxBytes=2*1024*1024;
const encoder=new TextEncoder();
// Bound both encoded JSON size and localStorage string payload, including Unicode.
const storageBytes=(raw:string)=>Math.max(encoder.encode(raw).byteLength,raw.length*2);
export class CardSnapshots<T>{
 private owner='';private values=new Map<string,T>();
 constructor(private readonly storageKey:string,private readonly read:(value:unknown,key:string)=>T|null){}
 align(owner:string):boolean{
  if(owner===this.owner)return false;
  if(this.owner)this.clear();else this.values.clear();this.owner=owner;
  if(!owner)return true;
  try{this.values=this.readStored();}catch{this.values.clear();this.removeStored();}
  return true;
 }
 clear(){this.owner='';this.values.clear();this.removeStored();}
 get(key:string):T|undefined{return this.values.get(key);}
 set(key:string,value:T){
  this.values.delete(key);this.values.set(key,value);
  if(this.values.size>maxEntries)this.values.delete(this.values.keys().next().value!);
  this.persist(stored=>{stored.delete(key);stored.set(key,value);});
 }
 forgetContext(prefix:string){
  for(const key of this.values.keys())if(key.startsWith(prefix))this.values.delete(key);
  this.persist(stored=>{for(const key of stored.keys())if(key.startsWith(prefix))stored.delete(key);});
 }
 delete(key:string){this.values.delete(key);this.persist(stored=>{stored.delete(key);});}
 private removeStored(){try{localStorage.removeItem(this.storageKey);}catch{/* Storage may be disabled. */}}
 private readStored():Map<string,T>{
  const values=new Map<string,T>(),raw=localStorage.getItem(this.storageKey);if(!raw)return values;
  if(raw.length>maxBytes||storageBytes(raw)>maxBytes)throw Error('Oversized cache');
  const stored=JSON.parse(raw) as {owner?:unknown;entries?:unknown};
  if(stored.owner!==this.owner||!Array.isArray(stored.entries)||stored.entries.length>maxEntries)throw Error('Invalid cache owner');
  for(const entry of stored.entries){
   if(!Array.isArray(entry)||entry.length!==2||typeof entry[0]!=='string'||entry[0].length>4096)throw Error('Invalid cache key');
   const value=this.read(entry[1],entry[0]);if(value)values.set(entry[0],value);
  }
  return values;
 }
 private persist(change:(stored:Map<string,T>)=>void){
  if(!this.owner)return;
  try{
   // Apply only this operation to current storage: another tab may have newer snapshots.
   let stored:Map<string,T>;try{stored=this.readStored();}catch{stored=new Map();}
   change(stored);
   while(stored.size>maxEntries)stored.delete(stored.keys().next().value!);
   let raw=JSON.stringify({owner:this.owner,entries:[...stored]});
   while(storageBytes(raw)>maxBytes&&stored.size){stored.delete(stored.keys().next().value!);raw=JSON.stringify({owner:this.owner,entries:[...stored]});}
   localStorage.setItem(this.storageKey,raw);
   // Capacity eviction must also remove local copies, without refreshing mounted metadata.
   for(const key of this.values.keys())if(!stored.has(key))this.values.delete(key);
  }catch{this.removeStored();/* Keep the mounted session cache when persistence is unavailable. */}
 }
}
